import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { parseDecimalText } from '@structura/ui'
import { SMBeamModel, SMPTCable, TendonBondType, PrestressEffect, PTCableLossBreakdown } from '../../types'
import { beamApiBase } from '@structura/services'
import { PT_COVER_MM, MIN_ECCENTRICITY_M, DEFAULT_FALLBACK_ECCENTRICITY_M, STRAND_PRESETS } from '@structura/calculations'
import { useTranslation } from '@structura/i18n'

interface PrestressingMaterialProps {
  fpk: number           // Characteristic tensile strength (MPa)
  fp01k: number         // 0.1% proof stress (MPa)
  elasticModulus: number // Elastic modulus (Pa)
  relaxationClass: number // 1, 2, or 3
  gammaS: number        // Partial factor
}

interface ConcreteProps {
  fck: number           // Characteristic strength (MPa)
  elasticModulus: number // Ecm (Pa)
  relativeHumidity: number // RH (%)
  notionalSize: number  // h0 (mm)
  loadingAge: number    // t0 (days)
  cementType: 'S' | 'N' | 'R'
}

interface CrossSectionProps {
  area: number          // Ac (m²)
  inertia: number       // Ic (m⁴)
  heightMm?: number     // Section height in mm
}

interface PTCableConfigPanelProps {
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  currentMaterial: string
  prestressingMaterial?: PrestressingMaterialProps
  concreteMaterial?: ConcreteProps
  crossSection?: CrossSectionProps
}

const PTCableConfigPanel: React.FC<PTCableConfigPanelProps> = ({
  model,
  setModel,
  currentMaterial,
  prestressingMaterial,
  concreteMaterial,
  crossSection
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  // Strand-based inputs
  const [strandCount, setStrandCount] = useState<string>('4')
  const [selectedStrandPreset, setSelectedStrandPreset] = useState<number>(2) // 15.2mm default
  const [strandAreaMm2, setStrandAreaMm2] = useState<string>('140')
  const [stressRatioPercent, setStressRatioPercent] = useState<string>('75') // 75% of max

  // Loss parameters
  const [frictionMu, setFrictionMu] = useState<string>('0.19')
  const [wobbleK, setWobbleK] = useState<string>('0.008')
  const [anchorageSlipMm, setAnchorageSlipMm] = useState<string>('6')
  const [bondType, setBondType] = useState<TendonBondType>('Grouted')

  const defaultMaxEcc = useMemo(() => {
    if (!crossSection?.heightMm || crossSection.heightMm <= 0) return DEFAULT_FALLBACK_ECCENTRICITY_M
    const heightM = crossSection.heightMm / 1000
    const coverM = PT_COVER_MM / 1000
    return Math.max(MIN_ECCENTRICITY_M, (heightM / 2) - coverM)
  }, [crossSection?.heightMm])

  // Profile. Empty text means "use the section default" (defaultMaxEcc, shown as the
  // placeholder) — nothing auto-writes into these fields; what the user types stays.
  const [interiorEcc, setInteriorEcc] = useState<string>('')
  const [midSpanEcc, setMidSpanEcc] = useState<string>('')
  const [endEcc, setEndEcc] = useState<string>('0')

  const spanCount = model.spans.length
  const nodeCount = model.nodes.length
  const ptCables = model.ptCables ?? []

  // Only show for concrete material
  const isConcreteBeam = currentMaterial === 'concrete'

  // Calculate max allowed stress per EC2 5.10.3
  const maxAllowedStressMPa = useMemo(() => {
    if (!prestressingMaterial) return 1400 // Default fallback
    const { fpk, fp01k } = prestressingMaterial
    // σp0 ≤ min(0.80 × fpk, 0.90 × fp0.1k)
    return Math.min(0.80 * fpk, 0.90 * fp01k)
  }, [prestressingMaterial])

  // Calculate P₀ from strand inputs (empty fields fall back to the shown defaults)
  const calculatedP0 = useMemo(() => {
    const count = parseDecimalText(strandCount) ?? 0
    const areaPerStrand = parseDecimalText(strandAreaMm2) ?? 140
    const stressRatio = (parseDecimalText(stressRatioPercent) ?? 75) / 100
    const sigma0 = stressRatio * maxAllowedStressMPa // MPa
    const totalArea = count * areaPerStrand // mm²
    // P₀ = Ap × σp0 (N) → kN
    return (totalArea * sigma0) / 1000
  }, [strandCount, strandAreaMm2, stressRatioPercent, maxAllowedStressMPa])

  // Calculate total tendon length (approximate)
  const tendonLength = useMemo(() => {
    return model.spans.reduce((sum, span) => sum + span.length, 0)
  }, [model.spans])

  // Calculate average eccentricity for loss calculation (empty = section default)
  const averageEccentricity = useMemo(() => {
    const interior = parseDecimalText(interiorEcc) ?? defaultMaxEcc
    const midSpan = parseDecimalText(midSpanEcc) ?? defaultMaxEcc
    // Weighted average: more weight on mid-span
    return (midSpan + interior) / 2
  }, [interiorEcc, midSpanEcc, defaultMaxEcc])

  // Loss preview state (calculated by backend)
  const [lossPreview, setLossPreview] = useState<PTCableLossBreakdown | null>(null)
  const [isCalculatingLoss, setIsCalculatingLoss] = useState(false)
  const [lossWarnings, setLossWarnings] = useState<string[]>([])
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch loss preview from backend API (debounced)
  const fetchLossPreview = useCallback(async () => {
    if (!prestressingMaterial || !concreteMaterial || !crossSection) {
      setLossPreview(null)
      return
    }
    if (calculatedP0 <= 0) {
      setLossPreview(null)
      return
    }

    const count = parseDecimalText(strandCount) ?? 0
    const areaPerStrand = parseDecimalText(strandAreaMm2) ?? 140
    if (count <= 0 || areaPerStrand <= 0) {
      setLossPreview(null)
      return
    }

    setIsCalculatingLoss(true)

    try {
      // PT cables / prestressing are a concrete-only feature.
      const response = await fetch(`${beamApiBase('concrete')}/pt-loss-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prestressForce: calculatedP0,
          strandCount: count,
          strandAreaMm2: areaPerStrand,
          frictionMu: parseDecimalText(frictionMu) ?? 0.19,
          wobbleCoefficientK: parseDecimalText(wobbleK) ?? 0.008,
          anchorageSlipMm: parseDecimalText(anchorageSlipMm) ?? 6,
          averageEccentricity,
          tendonLength,
          prestressing: {
            fpk: prestressingMaterial.fpk,
            fp01k: prestressingMaterial.fp01k,
            elasticModulus: prestressingMaterial.elasticModulus,
            relaxationClass: prestressingMaterial.relaxationClass
          },
          concrete: {
            fck: concreteMaterial.fck,
            elasticModulus: concreteMaterial.elasticModulus,
            relativeHumidity: concreteMaterial.relativeHumidity,
            notionalSize: concreteMaterial.notionalSize,
            loadingAge: concreteMaterial.loadingAge,
            cementType: concreteMaterial.cementType
          },
          crossSection: {
            area: crossSection.area,
            inertia: crossSection.inertia
          }
        })
      })

      const result = await response.json()

      if (result.success && result.breakdown) {
        setLossPreview(result.breakdown)
        setLossWarnings(result.warnings || [])
      } else {
        setLossPreview(null)
        setLossWarnings(result.error ? [result.error] : [])
      }
    } catch (err) {
      console.error('PT loss preview failed:', err)
      setLossPreview(null)
      setLossWarnings([t('beam.pt.failedToCalculate')])
    } finally {
      setIsCalculatingLoss(false)
    }
  }, [prestressingMaterial, concreteMaterial, crossSection, calculatedP0, strandCount, strandAreaMm2,
      frictionMu, wobbleK, anchorageSlipMm, averageEccentricity, tendonLength, t])

  // Debounced effect to fetch loss preview when inputs change
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchLossPreview()
    }, 300) // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [fetchLossPreview])

  const handleStrandPresetChange = (index: number) => {
    setSelectedStrandPreset(index)
    const preset = STRAND_PRESETS[index]
    if (preset) {
      setStrandAreaMm2(String(preset.areaMm2))
    }
  }

  const addPTCable = () => {
    const count = parseDecimalText(strandCount) ?? 0
    const areaPerStrand = parseDecimalText(strandAreaMm2) ?? 140
    const stressRatio = (parseDecimalText(stressRatioPercent) ?? 75) / 100
    const totalAreaMm2 = count * areaPerStrand
    const interior = parseDecimalText(interiorEcc) ?? defaultMaxEcc
    const midSpan = parseDecimalText(midSpanEcc) ?? defaultMaxEcc
    const end = parseDecimalText(endEcc) ?? 0

    // Build node eccentricities
    const nodeEccentricities: number[] = []
    for (let i = 0; i < nodeCount; i++) {
      if (i === 0 || i === nodeCount - 1) {
        nodeEccentricities.push(end)
      } else {
        nodeEccentricities.push(-interior) // Negative = above neutral axis
      }
    }

    // Build mid-span eccentricities
    const midSpanEccentricities: number[] = []
    for (let i = 0; i < spanCount; i++) {
      midSpanEccentricities.push(midSpan)
    }

    // Calculate loss ratio for simple storage
    const lossRatio = lossPreview ? lossPreview.totalLossPercent / 100 : 0

    const newCable: SMPTCable = {
      id: `ptcable-${Date.now()}`,
      prestressForce: calculatedP0,
      prestressLossRatio: lossRatio,
      strandCount: count,
      strandAreaMm2: areaPerStrand,
      stressRatio,
      cableAreaMm2: totalAreaMm2,
      frictionMu: parseDecimalText(frictionMu) ?? undefined,
      wobbleCoefficientK: parseDecimalText(wobbleK) ?? undefined,
      anchorageSlipMm: parseDecimalText(anchorageSlipMm) ?? undefined,
      bondType,
      nodeEccentricities,
      midSpanEccentricities,
      lossBreakdown: lossPreview ?? undefined
    }

    setModel(prev => ({
      ...prev,
      ptCables: [...(prev.ptCables ?? []), newCable]
    }))
  }

  const removePTCable = (cableId: string) => {
    setModel(prev => ({
      ...prev,
      ptCables: (prev.ptCables ?? []).filter(c => c.id !== cableId)
    }))
  }

  const formatCableDescription = (cable: SMPTCable): string => {
    const peff = cable.lossBreakdown?.effectiveForceKN ??
                 (cable.prestressForce * (1 - (cable.prestressLossRatio ?? 0)))
    const lossPercent = cable.lossBreakdown?.totalLossPercent ??
                        ((cable.prestressLossRatio ?? 0) * 100)
    const bondLabel = cable.bondType === 'Grouted' ? t('beam.pt.groutedLabel') : ''
    const strandInfo = cable.strandCount
      ? `${cable.strandCount}×${cable.strandAreaMm2}mm²`
      : `Ap=${cable.cableAreaMm2}mm²`

    return `P₀=${cable.prestressForce.toFixed(0)} kN → Peff=${peff.toFixed(0)} kN (−${lossPercent.toFixed(1)}%)${bondLabel} | ${strandInfo}`
  }

  if (!isConcreteBeam) {
    return null
  }

  const hasMaterialData = prestressingMaterial && concreteMaterial && crossSection

  return (
    <div className="card mb-3">
      <div
        className="card-header d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={t('beam.pt.sectionAria')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsExpanded(!isExpanded) }}
      >
        <span>
          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
          <i className="bi bi-bezier2 me-2"></i>
          {t('beam.pt.title')}
          {ptCables.length > 0 && (
            <span className="badge bg-primary ms-2">{ptCables.length}</span>
          )}
        </span>
      </div>

      {isExpanded && (
        <div className="card-body">
          {!hasMaterialData && (
            <div className="alert alert-warning mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t('beam.pt.materialNotLoaded')}
            </div>
          )}

          <p className="text-muted small mb-3">
            {t('beam.pt.intro')}
          </p>

          {/* Existing PT Cables */}
          {ptCables.length > 0 && (
            <div className="mb-3">
              <h6>{t('beam.pt.appliedCables')}</h6>
              <ul className="list-group">
                {ptCables.map(cable => (
                  <li key={cable.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      <i className="bi bi-bezier2 text-info me-2"></i>
                      {formatCableDescription(cable)}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removePTCable(cable.id)}
                      title={t('beam.pt.removeCable')}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Add New PT Cable */}
          <div className="border rounded p-3 bg-light">
            <h6>{t('beam.pt.addCable')}</h6>

            {/* Strand Selection */}
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label small fw-bold">{t('beam.pt.strandType')}</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedStrandPreset}
                  onChange={e => handleStrandPresetChange(parseInt(e.target.value))}
                >
                  {STRAND_PRESETS.map((preset, idx) => (
                    <option key={idx} value={idx}>
                      {preset.name} ({preset.areaMm2} mm²)
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">{t('beam.pt.numberOfStrands')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={strandCount}
                  onChange={e => setStrandCount(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">{t('beam.pt.strandArea')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={strandAreaMm2}
                  onChange={e => setStrandAreaMm2(e.target.value)}
                />
                <small className="text-muted">
                  {t('beam.pt.totalArea', { area: ((parseDecimalText(strandCount) ?? 0) * (parseDecimalText(strandAreaMm2) ?? 0)).toFixed(0) })}
                </small>
              </div>
            </div>

            {/* Stress Level */}
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label small fw-bold">{t('beam.pt.initialStressLevel')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={stressRatioPercent}
                  onChange={e => setStressRatioPercent(e.target.value)}
                />
                <small className="text-muted">
                  {t('beam.pt.percentOfMax', { max: maxAllowedStressMPa.toFixed(0) })}
                </small>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">{t('beam.pt.initialStress')}</label>
                <div className="form-control form-control-sm bg-white">
                  {((parseDecimalText(stressRatioPercent) ?? 75) / 100 * maxAllowedStressMPa).toFixed(0)} MPa
                </div>
                <small className="text-muted">{t('beam.pt.ec2Limit')}</small>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">{t('beam.pt.initialForce')}</label>
                <div className="form-control form-control-sm bg-white fw-bold text-primary">
                  {calculatedP0.toFixed(0)} kN
                </div>
                <small className="text-muted">{t('beam.pt.apSigma')}</small>
              </div>
            </div>

            {/* Loss Parameters */}
            <div className="row g-2 mb-3">
              <div className="col-12">
                <label className="form-label small fw-bold">
                  <i className="bi bi-graph-down me-1"></i>
                  {t('beam.pt.lossParameters')}
                </label>
              </div>
              <div className="col-md-3">
                <label className="form-label small">{t('beam.pt.frictionMu')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={frictionMu}
                  onChange={e => setFrictionMu(e.target.value)}
                />
                <small className="text-muted">{t('beam.pt.typ019')}</small>
              </div>
              <div className="col-md-3">
                <label className="form-label small">{t('beam.pt.wobbleK')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={wobbleK}
                  onChange={e => setWobbleK(e.target.value)}
                />
                <small className="text-muted">{t('beam.pt.typ0005')}</small>
              </div>
              <div className="col-md-3">
                <label className="form-label small">{t('beam.pt.anchorageSlip')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={anchorageSlipMm}
                  onChange={e => setAnchorageSlipMm(e.target.value)}
                />
                <small className="text-muted">{t('beam.pt.typ6mm')}</small>
              </div>
              <div className="col-md-3">
                <label className="form-label small">{t('beam.pt.bondType')}</label>
                <select
                  className="form-select form-select-sm"
                  value={bondType}
                  onChange={e => setBondType(e.target.value as TendonBondType)}
                >
                  <option value="Unbonded">{t('beam.pt.unbonded')}</option>
                  <option value="Grouted">{t('beam.pt.grouted')}</option>
                </select>
              </div>
            </div>

            {/* Prestress Effect (EC2 2.4.2.2) */}
            <div className="row g-2 mb-3">
              <div className="col-12">
                <label className="form-label small fw-bold">
                  <i className="bi bi-graph-up-arrow me-1"></i>
                  {t('beam.pt.prestressEffect')}
                </label>
              </div>
              <div className="col-md-6">
                <select
                  className="form-select form-select-sm"
                  value={model.prestressEffect ?? 'Favorable'}
                  onChange={e => setModel(prev => ({ ...prev, prestressEffect: e.target.value as PrestressEffect }))}
                >
                  <option value="Favorable">{t('beam.pt.favorableOption')}</option>
                  <option value="Unfavorable">{t('beam.pt.unfavorableOption')}</option>
                </select>
                <small className="text-muted">
                  {t('beam.pt.effectHint')}
                </small>
              </div>
              <div className="col-md-6">
                <div className="alert alert-info py-1 px-2 mb-0 small">
                  <i className="bi bi-info-circle me-1"></i>
                  <strong>{t('beam.pt.favorableLabel')}</strong> {t('beam.pt.favorableText')}
                  <strong className="ms-2">{t('beam.pt.unfavorableLabel')}</strong> {t('beam.pt.unfavorableText')}
                </div>
              </div>
            </div>

            {/* Profile Eccentricities */}
            <div className="row g-2 mb-3">
              <div className="col-12">
                <label className="form-label small fw-bold">
                  <i className="bi bi-bezier me-1"></i>
                  {t('beam.pt.cableProfile')}
                </label>
              </div>
              <div className="col-md-4">
                <label className="form-label small">{t('beam.pt.endSupportEcc')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={endEcc}
                  onChange={e => setEndEcc(e.target.value)}
                />
                <small className="text-muted">{t('beam.pt.typically0')}</small>
              </div>
              <div className="col-md-4">
                <label className="form-label small">{t('beam.pt.interiorSupportEcc')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={interiorEcc}
                  onChange={e => setInteriorEcc(e.target.value)}
                  placeholder={defaultMaxEcc.toFixed(3)}
                />
                <small className="text-muted">{t('beam.pt.aboveNA', { max: defaultMaxEcc.toFixed(3) })}</small>
              </div>
              <div className="col-md-4">
                <label className="form-label small">{t('beam.pt.midSpanEcc')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control form-control-sm"
                  value={midSpanEcc}
                  onChange={e => setMidSpanEcc(e.target.value)}
                  placeholder={defaultMaxEcc.toFixed(3)}
                />
                <small className="text-muted">{t('beam.pt.belowNA', { max: defaultMaxEcc.toFixed(3) })}</small>
              </div>
            </div>

            {/* Loss Preview */}
            {isCalculatingLoss && (
              <div className="card bg-white mb-3">
                <div className="card-body py-3 text-center">
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span className="visually-hidden">{t('beam.pt.calculating')}</span>
                  </div>
                  <small className="text-muted">{t('beam.pt.calculatingLosses')}</small>
                </div>
              </div>
            )}
            {!isCalculatingLoss && lossWarnings.length > 0 && !lossPreview && (
              <div className="alert alert-warning small mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {lossWarnings.join('; ')}
              </div>
            )}
            {!isCalculatingLoss && lossPreview && (
              <div className="card bg-white mb-3">
                <div className="card-header py-2">
                  <small className="fw-bold">
                    <i className="bi bi-calculator me-1"></i>
                    {t('beam.pt.lossPreview')}
                  </small>
                </div>
                {lossWarnings.length > 0 && (
                  <div className="alert alert-warning small mb-0 rounded-0 border-start-0 border-end-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {lossWarnings.join('; ')}
                  </div>
                )}
                <div className="card-body py-2">
                  <div className="row">
                    <div className="col-md-6">
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td colSpan={2} className="fw-bold text-muted">{t('beam.pt.immediateLosses')}</td>
                          </tr>
                          <tr>
                            <td className="ps-3">{t('beam.pt.friction')}</td>
                            <td className="text-end">{lossPreview.frictionLossKN.toFixed(1)} kN</td>
                          </tr>
                          <tr>
                            <td className="ps-3">{t('beam.pt.anchorageSlipLoss')}</td>
                            <td className="text-end">{lossPreview.anchorageSlipLossKN.toFixed(1)} kN</td>
                          </tr>
                          <tr>
                            <td className="ps-3">{t('beam.pt.elasticShortening')}</td>
                            <td className="text-end">{lossPreview.elasticShorteningLossKN.toFixed(1)} kN</td>
                          </tr>
                          <tr className="border-top">
                            <td className="ps-3 fw-bold">{t('beam.pt.subtotal')}</td>
                            <td className="text-end fw-bold">{lossPreview.totalImmediateLossKN.toFixed(1)} kN</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td colSpan={2} className="fw-bold text-muted">{t('beam.pt.timeDependent')}</td>
                          </tr>
                          <tr>
                            <td className="ps-3">{t('beam.pt.relaxation')}</td>
                            <td className="text-end">{lossPreview.relaxationLossKN.toFixed(1)} kN</td>
                          </tr>
                          <tr>
                            <td className="ps-3">{t('beam.pt.creep')}</td>
                            <td className="text-end">{lossPreview.creepLossKN.toFixed(1)} kN</td>
                          </tr>
                          <tr>
                            <td className="ps-3">{t('beam.pt.shrinkage')}</td>
                            <td className="text-end">{lossPreview.shrinkageLossKN.toFixed(1)} kN</td>
                          </tr>
                          <tr className="border-top">
                            <td className="ps-3 fw-bold">{t('beam.pt.subtotal')}</td>
                            <td className="text-end fw-bold">{lossPreview.totalTimeDependentLossKN.toFixed(1)} kN</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="row mt-2 pt-2 border-top">
                    <div className="col-md-4 text-center">
                      <small className="text-muted">{t('beam.pt.initialForceShort')}</small>
                      <div className="fw-bold">{calculatedP0.toFixed(0)} kN</div>
                    </div>
                    <div className="col-md-4 text-center">
                      <small className="text-muted">{t('beam.pt.totalLoss')}</small>
                      <div className="fw-bold text-danger">
                        −{lossPreview.totalLossKN.toFixed(0)} kN ({lossPreview.totalLossPercent.toFixed(1)}%)
                      </div>
                    </div>
                    <div className="col-md-4 text-center">
                      <small className="text-muted">{t('beam.pt.effectiveForce')}</small>
                      <div className="fw-bold text-success">{lossPreview.effectiveForceKN.toFixed(0)} kN</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary btn-sm mt-2"
              onClick={() => { console.log('Add PT Cable clicked'); addPTCable(); }}
              disabled={calculatedP0 <= 0}
              aria-label={t('beam.pt.addCableAria')}
              data-testid="add-pt-cable-button"
            >
              <i className="bi bi-plus me-1"></i>
              {t('beam.pt.addCable')}
            </button>
          </div>

          {spanCount === 1 && (
            <div className="alert alert-info mt-3 small mb-0">
              <i className="bi bi-info-circle me-2"></i>
              {t('beam.pt.singleSpanNote')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PTCableConfigPanel
